<template>
  <div class="be-map">
    <div class="be-map__intro">
      <h3 class="be-map__intro-title">{{ $t('hotel.map-title') }}</h3>
      <p class="be-map__intro-desc">
        {{ hasTabs ? $t('hotel.map-desc-multi') : $t('hotel.map-desc') }}
      </p>
    </div>

    <div class="be-map__head" v-if="hasTabs || canGoBack">
      <!-- Tabs stay mounted while drilled into a sub-map so a guest can jump
           straight to another main map instead of walking back up first. -->
      <div
        class="be-map__tabs hide-scroll"
        :class="{ 'be-map__tabs--overflow': is_tabs_overflowing }"
        v-if="hasTabs"
        ref="tabStrip"
        role="tablist"
        :aria-label="$t('hotel.map-select-area')">
        <button
          type="button"
          role="tab"
          class="be-map__tab be-press"
          :class="{ 'be-map__tab--active': activeRootMapId == map.id }"
          :aria-selected="activeRootMapId == map.id ? 'true' : 'false'"
          :ref="'tab-' + map.id"
          v-for="map in maps"
          :key="map.id"
          @click="selectRootMap(map.id)">
          {{ map.name }}
        </button>
      </div>

      <div class="be-map__crumbs" v-if="canGoBack">
        <template v-for="(crumb, idx) in breadcrumbTrail">
          <button
            v-if="idx < breadcrumbTrail.length - 1"
            :key="crumb.id + '-link'"
            type="button"
            class="be-map__crumb-link be-press"
            @click="goToCrumb(idx)">
            {{ crumb.name }}
          </button>
          <span v-else :key="crumb.id + '-current'" class="be-map__crumb-current">
            {{ crumb.name }}
          </span>
          <i
            v-if="idx < breadcrumbTrail.length - 1"
            :key="crumb.id + '-sep'"
            class="simple-icon-arrow-right be-map__crumb-sep"></i>
        </template>
      </div>
    </div>

    <div class="text-center py-5" v-if="is_detail_loading && !hasMapImage">
      <b-spinner variant="secondary"></b-spinner>
    </div>

    <template v-else-if="hasMapImage">
      <div class="be-map__stage">
        <transition name="be-map-crossfade">
          <MapViewport
            :image="active_map_detail"
            :areas="resolvedAreas"
            :reset-key="rendered_map_id"
            :key="rendered_map_id"
            @select-area="onSelectArea" />
        </transition>
        <!-- Drilling into an uncached map: the previous map stays on screen
             rather than unmounting into a blank stage while the next loads.
             Also covers the page's own search: the map is filter-independent,
             so without this the Search button looks like it did nothing. -->
        <div class="be-map__stage-loading" v-if="is_detail_loading || isSearching">
          <b-spinner variant="secondary"></b-spinner>
        </div>
      </div>

      <div class="be-map__legend">
        <span class="be-map__legend-item">
          <i class="be-map__legend-dot be-map__legend-dot--available"></i>
          {{ $t('hotel.map-legend-room') }}
        </span>
        <span class="be-map__legend-item" v-if="hasMapLink">
          <i class="be-map__legend-dot be-map__legend-dot--link"></i>
          {{ $t('hotel.map-legend-area') }}
        </span>
      </div>
    </template>

    <div class="text-center py-5" v-else>
      <p class="mb-0 be-map__empty">{{ $t('hotel.map-empty') }}</p>
    </div>
  </div>
</template>

<script>
  import MapViewport from './digital-map/MapViewport.vue';
  import {
    fetchMapDetail,
    fetchRoomTypeDetail,
    preloadImage,
    getCachedMapDetail,
    getCachedRoomTypeDetail,
    hasCachedRoomTypeDetail
  } from './digital-map/mapDataCache.js';
  import {
    pushHistoryStep,
    releaseHistoryStep,
    abandonHistorySteps
  } from '@/utils/historyStep.js';

  // breathing room kept either side of the active tab, matched to the CSS fade
  const TAB_SCROLL_MARGIN = 24;

  export default {
    name: 'BeDigitalMap',
    components: {
      MapViewport
    },
    props: {
      maps: {
        type: Array,
        default: () => []
      },
      merchantId: {
        type: [String, Number],
        default: ''
      },
      // owned by the page so the choice survives this component unmounting;
      // synced back through update:selectedMapId
      selectedMapId: {
        type: [String, Number],
        default: null
      },
      // the page is running a search-room request; the map itself never
      // changes with the filter, but the guest still needs a sign that the
      // Search button did something
      isSearching: {
        type: Boolean,
        default: false
      }
    },
    data(){
      return {
        active_map_id: null,
        active_map_name: '',
        active_map_detail: null,
        /* Set only alongside active_map_detail, never alongside active_map_id:
           for an uncached map active_map_id changes the instant a tab/pin is
           clicked, well before the fetch+preload resolves. Keying MapViewport
           off active_map_id directly used to remount it immediately while it
           was still bound to the OLD image, so the crossfade played against
           itself and the real image swap landed later with no transition at
           all -- this tracks whichever map id is actually on screen right now. */
        rendered_map_id: null,
        is_detail_loading: false,
        /* room type detail keyed by id, the sole source for pin labels. An id
           absent from this map is still loading; an id mapped to null is a room
           type the API no longer knows about. */
        room_type_map: {},
        // ancestor {id, name, historyStep} entries, so a drill-down can walk
        // back up and render a breadcrumb rather than a bare, contextless
        // "Back" button -- historyStep is the browser entry that mirrors it
        nav_stack: [],
        is_tabs_overflowing: false
      }
    },
    computed: {
      hasTabs(){
        return this.maps.length > 1;
      },
      // While drilled into a sub-map active_map_id is the child's, so the tab
      // strip needs the root the guest came in through to stay highlighted.
      activeRootMapId(){
        return this.nav_stack.length > 0 ? this.nav_stack[0].id : this.active_map_id;
      },
      canGoBack(){
        return this.nav_stack.length > 0;
      },
      breadcrumbTrail(){
        return this.nav_stack.concat([{ id: this.active_map_id, name: this.active_map_name }]);
      },
      hasMapImage(){
        return !!(this.active_map_detail && this.active_map_detail.image_url);
      },
      hasMapLink(){
        return this.resolvedAreas.some((item) => item.kind == 'map_link');
      },
      resolvedAreas(){
        if(!this.active_map_detail || !this.$isNotEmptyV2(this.active_map_detail.areas)) return [];
        return this.active_map_detail.areas
          .filter((area) => area.room_type_id || area.child_merchant_booking_map_id)
          .map((area) => {
            if(area.child_merchant_booking_map_id) return this.buildMapLinkArea(area);
            return this.buildRoomTypeArea(area);
          });
      }
    },
    watch: {
      maps(){
        this.$nextTick(this.measureTabsOverflow);
        // a re-search can drop the map that was open -- only keep it if it survived
        if(this.active_map_id && this.maps.some((map) => map.id == this.activeRootMapId)) return;
        this.openInitialMap();
      },
      activeRootMapId(mapId){
        this.$emit('update:selectedMapId', mapId);
        this.$nextTick(() => this.scrollActiveTabIntoView());
      }
    },
    created(){
      // plain instance properties: a request sequence number and a running
      // minimum are bookkeeping, not view state
      this.detail_request_token = 0;
      this.lowest_price = 0;
      /* Runs here rather than mounted(): a map already fetched this session
         (guest switches to another tab and back) is read from mapDataCache
         synchronously, so the very first render already shows it instead of a
         blank/spinner frame that then flips over once mounted() catches up. */
      this.openInitialMap();
    },
    mounted(){
      this.$nextTick(this.measureTabsOverflow);
      window.addEventListener('resize', this.measureTabsOverflow);
    },
    beforeDestroy(){
      window.removeEventListener('resize', this.measureTabsOverflow);
      /* Drops the trail's browser entries without rewinding: this also runs on
         a route change, where history.go() would fight the navigation already
         in flight. Anything left over is skipped on the way back. */
      abandonHistorySteps();
    },
    methods: {
      buildBaseArea(area, kind){
        return {
          area: area,
          kind: kind,
          roomTypeId: null,
          targetMapId: null,
          clickable: false,
          isPending: false,
          labelTitle: '',
          preview: null
        };
      },
      buildMapLinkArea(area){
        /* The map-list endpoint only returns root maps, so a drill-down target
           (almost always non-root) cannot be found there -- use the name the API
           denormalises onto the area itself, falling back to the list only for
           older cached responses that predate that field. */
        let targetName = area.child_merchant_booking_map_name;
        if(!targetName){
          const target = this.maps.find((map) => map.id == area.child_merchant_booking_map_id);
          targetName = target ? target.name : '';
        }
        const item = this.buildBaseArea(area, 'map_link');
        item.targetMapId = area.child_merchant_booking_map_id;
        item.clickable = true;
        item.labelTitle = targetName || '';
        return item;
      },
      buildRoomTypeArea(area){
        const item = this.buildBaseArea(area, 'room_type');
        item.roomTypeId = area.room_type_id;

        /* Three states, and the distinction matters: an id not yet in
           room_type_map is still in flight and gets a placeholder pin, while an
           id mapped to null is a room type the API dropped and stays muted. */
        const hasEntry = Object.prototype.hasOwnProperty.call(
          this.room_type_map,
          area.room_type_id
        );
        if(!hasEntry){
          item.isPending = true;
          return item;
        }

        const detail = this.room_type_map[area.room_type_id];
        if(!detail) return item;

        item.clickable = true;
        item.labelTitle = detail.name;
        item.preview = this.buildAreaPreview(detail);
        return item;
      },
      /* The pin itself stays minimal -- name only. Everything here is for the
         hover card, and none of it is date-aware: room-type takes no stay
         dates, so the price is a "from" figure and the popup is what shows the
         real, filtered rate. */
      buildAreaPreview(detail){
        const images = detail.image || [];
        const firstImage = images.length > 0 ? images[0] : null;
        const priceFrom = this.resolvePreviewPrice(detail);
        return {
          image: (firstImage && firstImage.image_url) || '',
          priceFrom: priceFrom > 0 ? this.$formatCurrency(priceFrom) : '',
          meta: this.buildPreviewMeta(detail)
        };
      },
      buildPreviewMeta(detail){
        const parts = [];
        if(detail.max_guests > 0){
          parts.push(detail.max_guests + ' ' + this.$pluralWord(detail.max_guests, 'Guest'));
        }
        if(detail.room_size && detail.room_size != '0') parts.push(detail.room_size + 'm²');
        return parts.join(' · ');
      },
      resolvePreviewPrice(detail){
        const lower = detail.lower_price;
        if(lower && lower.parsedValue) return lower.parsedValue;
        const best = detail.best_price;
        if(best && best.final_price && best.final_price.parsedValue) {
          return best.final_price.parsedValue;
        }
        return 0;
      },
      /* One request per distinct room type, deduped and cached in mapDataCache,
         so a room type appearing on several maps is only ever fetched once
         per session -- a second map (or a remount of this one) that shows the
         same room type reads it back synchronously instead of re-fetching. */
      hydrateRoomTypes(areas){
        const pendingIds = areas
          .map((area) => area.room_type_id)
          .filter((roomTypeId, index, ids) => {
            if(!roomTypeId) return false;
            if(ids.indexOf(roomTypeId) != index) return false;
            return !Object.prototype.hasOwnProperty.call(this.room_type_map, roomTypeId);
          });

        pendingIds.forEach((roomTypeId) => {
          if(hasCachedRoomTypeDetail(this.merchantId, roomTypeId)){
            const cached = getCachedRoomTypeDetail(this.merchantId, roomTypeId);
            this.$set(this.room_type_map, roomTypeId, cached);
            this.reportStartingPrice(cached);
            return;
          }
          fetchRoomTypeDetail(this.merchantId, roomTypeId).then((detail) => {
            this.$set(this.room_type_map, roomTypeId, detail);
            this.reportStartingPrice(detail);
          });
        });
      },
      /* The page has no room list to derive a "starting from" price from while
         the map is the only result view, so the cheapest room type the map has
         hydrated stands in for it. */
      reportStartingPrice(detail){
        if(!detail) return;
        const price = this.resolvePreviewPrice(detail);
        if(price <= 0) return;
        if(this.lowest_price > 0 && price >= this.lowest_price) return;
        this.lowest_price = price;
        this.$emit('starting-price', price);
      },
      openInitialMap(){
        if(this.maps.length == 0) return;
        const remembered = this.selectedMapId
          ? this.maps.find((map) => map.id == this.selectedMapId)
          : null;
        const target = remembered || this.maps.find((map) => map.is_root) || this.maps[0];
        this.resetTrail();
        this.loadMapDetail(target.id, target.name);
      },
      selectRootMap(mapId){
        // tapping the active tab is a no-op at the root, but means "go back up"
        // when the guest has drilled into one of its sub-maps
        if(this.activeRootMapId == mapId && this.nav_stack.length == 0) return;
        const map = this.maps.find((item) => item.id == mapId);
        this.resetTrail();
        this.loadMapDetail(mapId, map ? map.name : '');
      },
      /* Empties the trail and rewinds the browser past every entry it owned,
         so a jump straight back to a root map does not leave dead Back presses
         behind. Releasing the bottom step takes the ones above it with it. */
      resetTrail(){
        if(this.nav_stack.length == 0) return;
        const bottomStep = this.nav_stack[0].historyStep;
        this.nav_stack = [];
        releaseHistoryStep(bottomStep);
      },
      /* Nudges scrollLeft by hand rather than calling scrollIntoView: that also
         scrolls the page vertically, and this component mounts while the guest
         is often still above the results. */
      scrollActiveTabIntoView(){
        const strip = this.$refs.tabStrip;
        const tabs = this.$refs['tab-' + this.activeRootMapId];
        const tab = Array.isArray(tabs) ? tabs[0] : tabs;
        if(!strip || !tab) return;
        const stripBox = strip.getBoundingClientRect();
        const tabBox = tab.getBoundingClientRect();
        const leftGap = tabBox.left - stripBox.left;
        const rightGap = tabBox.right - stripBox.right;
        if(leftGap < TAB_SCROLL_MARGIN) strip.scrollLeft += leftGap - TAB_SCROLL_MARGIN;
        else if(rightGap > -TAB_SCROLL_MARGIN) strip.scrollLeft += rightGap + TAB_SCROLL_MARGIN;
      },
      measureTabsOverflow(){
        const strip = this.$refs.tabStrip;
        this.is_tabs_overflowing = !!strip && strip.scrollWidth > strip.clientWidth + 1;
      },
      // Jumps straight to an ancestor from the breadcrumb -- goBack() one level
      // is just the special case where idx is the second-to-last crumb.
      goToCrumb(idx){
        if(idx >= this.nav_stack.length) return;
        const target = this.nav_stack[idx];
        this.nav_stack = this.nav_stack.slice(0, idx);
        this.loadMapDetail(target.id, target.name);
        releaseHistoryStep(target.historyStep);
      },
      /* Browser Back, whose history entry is already gone -- so unlike
         goToCrumb this must never rewind. The guard keeps a release that pops
         several entries at once from re-entering: every step above the one the
         caller handled finds the trail already shorter and does nothing. */
      popToLevel(level){
        if(this.nav_stack.length <= level) return;
        const target = this.nav_stack[level];
        this.nav_stack = this.nav_stack.slice(0, level);
        this.loadMapDetail(target.id, target.name);
      },
      onSelectArea(item){
        if(item.kind == 'map_link'){
          if(this.active_map_id){
            const level = this.nav_stack.length;
            this.nav_stack.push({
              id: this.active_map_id,
              name: this.active_map_name,
              historyStep: pushHistoryStep(() => this.popToLevel(level))
            });
          }
          this.loadMapDetail(item.targetMapId, item.labelTitle);
          return;
        }
        this.$emit('select-room-type', { id: item.roomTypeId, name: item.labelTitle });
      },
      loadMapDetail(mapId, mapName){
        /* Token guard: rapid tab clicks used to let a slow earlier response land
           last and overwrite the map the guest is actually looking at. */
        this.detail_request_token += 1;
        const requestToken = this.detail_request_token;

        this.active_map_id = mapId;
        this.active_map_name = mapName || '';

        /* Already fetched this session -- e.g. revisiting a map after
           drilling into a link, or remounting after a tab switch. Swap it in
           synchronously rather than re-running the fetch/preload chain, so
           switching views never re-shows a spinner for data already in hand. */
        const cachedDetail = getCachedMapDetail(mapId);
        if(cachedDetail){
          this.is_detail_loading = false;
          // a fetch this instance abandoned mid-flight (token guard below)
          // still lands here on resolve without ever decoding its image
          preloadImage(cachedDetail.image_url);
          this.active_map_detail = cachedDetail;
          this.rendered_map_id = mapId;
          this.hydrateRoomTypes(cachedDetail.areas || []);
          return;
        }

        // not resolved yet -- either a fresh request or one already in
        // flight (fetchMapDetail dedupes either way)
        this.is_detail_loading = true;

        fetchMapDetail(mapId)
          .then((detail) => {
            if(requestToken != this.detail_request_token) return null;
            // hold the old map on screen until the new image is decoded,
            // otherwise the swap animates into an empty frame
            if(detail) return preloadImage(detail.image_url).then(() => detail);
            return null;
          })
          .then((detail) => {
            if(requestToken != this.detail_request_token) return;
            this.is_detail_loading = false;
            if(!detail) return;
            this.active_map_detail = detail;
            this.rendered_map_id = mapId;
            /* Map-link areas need no API call of their own -- their name comes
               straight off the area (child_merchant_booking_map_name) via
               buildMapLinkArea. Only room-type areas hit the network, since
               their hover preview needs an image and price the area data
               doesn't carry. */
            this.hydrateRoomTypes(detail.areas || []);
          });
      }
    }
  }
</script>

<style scoped>
/* No card/border on purpose -- the map image and its pins carry enough visual
   weight on their own, a surrounding card just doubled up the framing. */
.be-map {
  margin-bottom: 20px;
}
/* Guests land on this view by default whenever a map exists, before they've
   used it -- a short blurb up top is what tells them the shapes are clickable
   room areas rather than just a decorative image. */
.be-map__intro {
  margin-bottom: 14px;
}
.be-map__intro-title {
  margin-bottom: 4px;
  font-family: var(--be-v3-title-font, BeV3Sans), sans-serif;
  font-size: 1.0625rem;
  font-weight: 700;
  color: #1a1a2e;
}
.be-map__intro-desc {
  margin-bottom: 0;
  font-size: 0.8125rem;
  color: #6d6d6d;
}
/* Column, not row: the tab strip and the breadcrumb are shown together while
   drilled in, and side by side the tabs would eat the breadcrumb's width. */
.be-map__head {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
}
/* Breadcrumb replaces a bare "Back" button: every ancestor stays one click
   away and the current map's name is always in view, instead of a lone arrow
   that gives no sense of where "back" actually leads. */
.be-map__crumbs {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
}
.be-map__crumb-link {
  padding: 4px 8px;
  border: none;
  border-radius: var(--be-v3-button-radius, 6px);
  background: transparent;
  color: #6d6d6d;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
}
.be-map__crumb-link:hover {
  background: #f4f5f7;
  color: #1a1a2e;
}
.be-map__crumb-current {
  padding: 4px 8px;
  color: #1a1a2e;
  font-size: 0.8125rem;
  font-weight: 700;
}
.be-map__crumb-sep {
  font-size: 8px;
  color: #adb5bd;
}
.be-map__tabs {
  display: flex;
  max-width: 100%;
  gap: 8px;
  overflow-x: auto;
}
/* hide-scroll removes the scrollbar, so a strip that overflows looks like it
   simply ends -- the fade is the only cue that more maps sit off to the right.
   Applied only while actually overflowing, or it would dim the last tab of a
   strip that fits. */
.be-map__tabs--overflow {
  -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 24px), transparent);
  mask-image: linear-gradient(to right, #000 calc(100% - 24px), transparent);
}
.be-map__tab {
  flex-shrink: 0;
  padding: 8px 16px;
  border: 1px solid var(--be-v3-border, #d5d9e0);
  border-radius: var(--be-v3-button-radius, 999px);
  background: #fff;
  color: #444;
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}
.be-map__tab--active {
  color: var(--merchant-primary-color, #79a33a);
  border-color: var(--merchant-primary-color, #79a33a);
  background: var(--merchant-primary-color-alpha-15, rgba(121, 163, 58, 0.12));
}
.be-map__stage {
  position: relative;
  /* clips the crossfade's outgoing layer, whose absolute-positioned height
     can briefly exceed the incoming layer's during the overlap */
  overflow: hidden;
  border-radius: var(--be-v3-radius, 12px);
}
.be-map__stage-loading {
  position: absolute;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.55);
}
/* MAIN <-> DETAIL MAP SWAP -----------------------------------------------
   Old and new map zoom-fade in place: the entering map grows in from 104%
   (reads as "diving into" the new area/floor), the leaving one shrinks to
   96% as it fades. The leaving layer is pulled out of flow (absolute) so
   it can overlap the entering one instead of stacking beneath it and
   doubling the stage's height; the entering layer stays in normal flow so
   it alone sets the stage's height. Leave fades faster than enter (matches
   .be-swap-* in be-v3-motion.css) to shrink the overlap window where an
   uncached nav can briefly show two identical frames. */
.be-map-crossfade-enter-active {
  transition: opacity var(--be-v3-dur-base, 220ms) var(--be-v3-ease, ease-out),
    transform var(--be-v3-dur-base, 220ms) var(--be-v3-ease, ease-out);
  z-index: 2;
}
.be-map-crossfade-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1;
  transition: opacity 120ms var(--be-v3-ease, ease-out),
    transform 120ms var(--be-v3-ease, ease-out);
}
.be-map-crossfade-enter {
  opacity: 0;
  transform: scale(1.04);
}
.be-map-crossfade-leave-to {
  opacity: 0;
  transform: scale(0.96);
}
.be-map__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 14px;
  font-size: 0.75rem;
  color: #6d6d6d;
}
.be-map__legend-item {
  display: inline-flex;
  align-items: center;
}
.be-map__legend-dot {
  width: 10px;
  height: 10px;
  margin-right: 6px;
  border-radius: 3px;
  border: 1px solid currentColor;
}
.be-map__legend-dot--available {
  color: var(--merchant-primary-color, #79a33a);
  background: var(--merchant-primary-color-alpha-15, rgba(121, 163, 58, 0.16));
}
.be-map__legend-dot--link {
  color: #1a1a2e;
  background: rgba(26, 26, 46, 0.12);
}
.be-map__empty {
  font-size: 0.875rem;
  font-weight: 600;
  color: #6d6d6d;
}
.be-v3-content-font-default .be-map__tab,
.be-v3-content-font-default .be-map__crumb-link,
.be-v3-content-font-default .be-map__crumb-current {
  font-weight: 500;
}
@media screen and (max-width: 767.98px) {
  .be-map__head {
    gap: 6px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/BeDigitalMap.vue